/**
 * Notification sounds (RUT-3168)
 *
 * Short cues for the two alerts an office actually wants to hear across the
 * room: money landing, and a new customer signing up.
 *
 * Synthesized with the Web Audio API rather than shipped as audio files. Two
 * reasons: no binary asset to license, host, or cache-bust, and the cues stay
 * tunable in code (a bell's partials are a few numbers here, not a re-export
 * from a DAW). Everything below is a handful of oscillators and one noise
 * burst — a few hundred bytes of JS against ~40KB of MP3.
 */

const SOUND_PREFERENCE_KEY = 'ruta.notificationSound.enabled';

/** Fired on the window whenever the mute preference changes, so open UI syncs. */
export const NOTIFICATION_SOUND_PREFERENCE_EVENT = 'ruta:notification-sound-preference';

/** Master level. Deliberately quiet — this plays while people are on the phone. */
const MASTER_GAIN = 0.22;

let audioContext: AudioContext | null = null;

/**
 * Whether payment/registration cues should play. Defaults to on; the preference
 * only ever persists an explicit opt-out or opt-in.
 */
export function isNotificationSoundEnabled(): boolean {
  try {
    return window.localStorage.getItem(SOUND_PREFERENCE_KEY) !== 'false';
  } catch {
    // Private browsing / storage disabled — fall back to the default rather
    // than letting a storage exception break the notification handler.
    return true;
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(SOUND_PREFERENCE_KEY, enabled ? 'true' : 'false');
  } catch {
    // Preference simply won't survive the session; playback still respects the
    // in-memory choice for this tab via the event below.
  }
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_SOUND_PREFERENCE_EVENT, { detail: { enabled } }),
  );
}

/**
 * Lazily create the shared AudioContext. Browsers refuse to start one before a
 * user gesture, and a context created too early lands in 'suspended' — so we
 * build it on first play and nudge it awake, giving up quietly if the browser
 * says no.
 */
function getAudioContext(): AudioContext | null {
  const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
  if (!Ctor) return null;

  if (!audioContext) {
    try {
      audioContext = new Ctor();
    } catch {
      return null;
    }
  }

  if (audioContext.state === 'suspended') {
    void audioContext.resume().catch(() => {
      /* No gesture yet — this cue is skipped, the next one will likely play. */
    });
  }

  return audioContext;
}

/**
 * A single struck-bell partial: a sine that starts at full level and rings out.
 * Struck instruments have no attack ramp to speak of and an exponential decay,
 * which is what separates a "ching" from a synthy "beep".
 */
function strikePartial(
  ctx: AudioContext,
  { frequency, startAt, duration, gain }: { frequency: number; startAt: number; duration: number; gain: number },
): void {
  const osc = ctx.createOscillator();
  const amp = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.setValueAtTime(frequency, startAt);

  amp.gain.setValueAtTime(0, startAt);
  // 4ms attack: fast enough to read as a strike, slow enough to avoid a click.
  amp.gain.linearRampToValueAtTime(gain * MASTER_GAIN, startAt + 0.004);
  amp.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  osc.connect(amp).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

/**
 * The mechanical part of the register: the drawer/hammer hit. Filtered white
 * noise, very short — this is the "ka" that makes the following bell read as a
 * cash register rather than a doorbell.
 */
function drawerHit(ctx: AudioContext, startAt: number): void {
  const duration = 0.06;
  const frameCount = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let i = 0; i < frameCount; i++) {
    // Pre-decayed noise so the burst is a hit, not a hiss.
    samples[i] = (Math.random() * 2 - 1) * (1 - i / frameCount);
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;

  // Band-pass around 2.5kHz: metallic, without the full-spectrum "shh".
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(2500, startAt);
  filter.Q.setValueAtTime(1.2, startAt);

  const amp = ctx.createGain();
  // Noise energy is spread across the pass band, so its peak sits far below an
  // oscillator at the same nominal gain. Rendered offline, 0.5 put the hit ~8x
  // under the bell — effectively inaudible. 2.2 lands it around a third of the
  // bell's peak: clearly the "ka", still clearly not the loudest thing.
  amp.gain.setValueAtTime(2.2 * MASTER_GAIN, startAt);
  amp.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  source.connect(filter).connect(amp).connect(ctx.destination);
  source.start(startAt);
  source.stop(startAt + duration);
}

/**
 * "Ka-ching" — the drawer hit, then a bell struck a beat later.
 *
 * The bell is a C6 chord stack with a deliberately inharmonic upper partial;
 * real bells are inharmonic, and a perfectly harmonic stack sounds like an
 * organ instead.
 */
export function playCashRegister(): void {
  if (!isNotificationSoundEnabled() || document.hidden) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime + 0.01;

  drawerHit(ctx, now);

  const bellAt = now + 0.085;
  strikePartial(ctx, { frequency: 1046.5, startAt: bellAt, duration: 0.9, gain: 0.9 });  // C6 — fundamental
  strikePartial(ctx, { frequency: 1568.0, startAt: bellAt, duration: 0.8, gain: 0.5 });  // G6
  strikePartial(ctx, { frequency: 2093.0, startAt: bellAt, duration: 0.6, gain: 0.3 });  // C7
  strikePartial(ctx, { frequency: 2794.0, startAt: bellAt, duration: 0.35, gain: 0.16 }); // inharmonic shimmer
}

/**
 * A rising four-note arpeggio for a new customer registration — warm and
 * welcoming, and clearly not the register, so the two are never confused from
 * across the room.
 */
export function playNewCustomerChime(): void {
  if (!isNotificationSoundEnabled() || document.hidden) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime + 0.01;
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 – E5 – G5 – C6

  notes.forEach((frequency, index) => {
    const startAt = now + index * 0.085;
    strikePartial(ctx, { frequency, startAt, duration: index === notes.length - 1 ? 0.7 : 0.4, gain: 0.75 });
    // A soft octave above each note keeps the arpeggio bright without making it
    // as metallic as the register bell.
    strikePartial(ctx, { frequency: frequency * 2, startAt, duration: 0.25, gain: 0.14 });
  });
}

/**
 * Play a cue at most once per alert.
 *
 * Two things make an alert arrive more than once: `useWebSocket` is mounted in
 * both the app shell and the role router, so each notification is handled twice;
 * and the socket itself is at-least-once. A duplicate toast is invisible (the
 * toaster collapses it) but a duplicate ka-ching is a very audible flam at
 * double amplitude. Keyed on the notification id, so two genuinely separate
 * payments still ring twice.
 */
const recentCues = new Map<string, number>();
const CUE_DEDUPE_MS = 3000;

export function playAlertCueOnce(key: string, play: () => void): void {
  const now = Date.now();
  for (const [seen, at] of recentCues) {
    if (now - at > CUE_DEDUPE_MS) recentCues.delete(seen);
  }
  if (recentCues.has(key)) return;
  recentCues.set(key, now);
  play();
}

/** Test-only: drop the cached context so each test starts clean. */
export function __resetAudioContextForTests(): void {
  audioContext = null;
  recentCues.clear();
}
